import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import ResponsiveModalAbsolute from "../../../components/ResponsiveModalAbsolute";
import Spinner from "../../../components/helpers/Spinner";
import Swal from "sweetalert2";
import { fetchAxiosToken } from "../../../helpers/fetchAxiosToken";

type Huesped = {
    id: number;
    id_pedido_habitacion: number;
    nombre_completo: string;
    tipo_documento: string;
    numero_documento: string;
    celular?: string;
    email?: string;
    imagen_documento?: string;
    dni?: string;
};

interface Props {
    modalVisible: boolean;
    setModalVisible: (v: boolean) => void;
    idPedidoHabitacion: number;
}

type CreateForm = {
    nombre_completo: string;
    tipo_documento: string;
    numero_documento: string;
    celular: string;
    email: string;
};

const initialForm: CreateForm = {
    nombre_completo: "",
    tipo_documento: "",
    numero_documento: "",
    celular: "",
    email: "",
};

const TIPO_DOC_OPCIONES = [
    "DNI",
    "Pasaporte",
    "Licencia de conducir",
    "Otro",
] as const;

const CONTROL =
    "w-full h-10 px-3 rounded-lg border border-[#E9E9E9] bg-white outline-none text-sm " +
    "focus:ring-2 focus:ring-[#1C4CBB]/40 focus:border-[#1C4CBB]";

export const ModalHuespedes: React.FC<Props> = ({
    modalVisible,
    setModalVisible,
    idPedidoHabitacion,
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [list, setList] = useState<Huesped[]>([]);

    const [showCreate, setShowCreate] = useState(false);

    const [createForm, setCreateForm] = useState<CreateForm>(initialForm);
    const [createFile, setCreateFile] = useState<File | null>(null);
    const [createPreview, setCreatePreview] = useState<string | null>(null);

    const [editing, setEditing] = useState<null | Huesped>(null);
    const [editFile, setEditFile] = useState<File | null>(null);
    const [editPreview, setEditPreview] = useState<string | null>(null);

    const [viewImageUrl, setViewImageUrl] = useState<string | null>(null);

    const apiBase = useMemo(() => `/huespedes_hotel_habitaciones`, []);

    const normalizeItem = (raw: any): Huesped => ({
        id: raw.id,
        id_pedido_habitacion: raw.id_pedido_habitacion,
        nombre_completo: raw.nombre_completo ?? "",
        tipo_documento: raw.tipo_documento ?? "",
        numero_documento: raw.numero_documento ?? raw.dni ?? "",
        celular: raw.celular ?? "",
        email: raw.email ?? "",
        imagen_documento: raw.imagen_documento ?? raw.imagen ?? "",
    });

    const getAll = async () => {
        try {
            setLoading(true);
            const data = await fetchAxiosToken({
                url: `${apiBase}/getAll/${idPedidoHabitacion}`,
                method: "get",
            });
            const arr = Array.isArray(data) ? data : data?.payload ?? [];
            setList(arr.map(normalizeItem));
        } catch {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text: "No se pudo obtener el listado de huéspedes.",
                icon: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (modalVisible && idPedidoHabitacion) getAll();
    }, [modalVisible, idPedidoHabitacion]);

    useEffect(() => {
        if (createFile) {
            const url = URL.createObjectURL(createFile);
            setCreatePreview(url);
            return () => URL.revokeObjectURL(url);
        }
        setCreatePreview(null);
    }, [createFile]);

    useEffect(() => {
        if (editFile) {
            const url = URL.createObjectURL(editFile);
            setEditPreview(url);
            return () => URL.revokeObjectURL(url);
        }
        setEditPreview(null);
    }, [editFile]);

    const resetCreate = () => {
        setCreateForm(initialForm);
        setCreateFile(null);
        setCreatePreview(null);
    };

    const validateCreateForm = (f: CreateForm) => {
        if (!f.nombre_completo.trim())
            return "El nombre completo es requerido.";
        if (f.nombre_completo.trim().length < 3) return "Nombre muy corto.";
        if (!f.tipo_documento) return "Selecciona el tipo de documento.";
        if (!f.numero_documento.trim())
            return "El número de documento es requerido.";
        if (f.email && !/^\S+@\S+\.\S+$/.test(f.email))
            return "Email inválido.";
        return null;
    };

    const validateEditForm = (f: Huesped) => {
        if (!f.nombre_completo.trim())
            return "El nombre completo es requerido.";
        if (f.nombre_completo.trim().length < 3) return "Nombre muy corto.";
        if (!f.tipo_documento.trim())
            return "El tipo de documento es requerido.";
        if (!f.numero_documento.trim())
            return "El número de documento es requerido.";
        if (f.email && !/^\S+@\S+\.\S+$/.test(f.email))
            return "Email inválido.";
        return null;
    };

    const handleCreate = async () => {
        const errMsg = validateCreateForm(createForm);
        if (errMsg) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Dato inválido",
                text: errMsg,
                icon: "error",
            });
            return;
        }

        try {
            setSaving(true);
            const fd = new FormData();
            fd.append("id_pedido_habitacion", String(idPedidoHabitacion));
            fd.append("nombre_completo", createForm.nombre_completo.trim());
            fd.append("tipo_documento", createForm.tipo_documento);
            fd.append("numero_documento", createForm.numero_documento.trim());
            if (createForm.celular.trim())
                fd.append("celular", createForm.celular.trim());
            if (createForm.email.trim())
                fd.append("email", createForm.email.trim());
            if (createFile) fd.append("imagen_documento_huesped", createFile);

            await fetchAxiosToken({
                url: `${apiBase}/createHuesped`,
                method: "post",
                body: fd,
            });

            await getAll();
            resetCreate();
            setShowCreate(false);

            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Creado",
                text: "Huésped agregado correctamente.",
                icon: "success",
            });
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ?? "No se pudo crear el huésped.";
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text: String(msg),
                icon: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        const res = await Swal.fire({
            confirmButtonColor: "#029BFC",
            iconColor: "#029BFC",
            title: "¿Eliminar huésped?",
            text: "Esta acción no se puede deshacer.",
            icon: "warning",
            showCancelButton: true,
            cancelButtonText: "Cancelar",
            confirmButtonText: "Eliminar",
        });
        if (!res.isConfirmed) return;

        try {
            setSaving(true);
            await fetchAxiosToken({
                url: `${apiBase}/deleteHuesped/${id}`,
                method: "delete",
            });
            await getAll();
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Eliminado",
                text: "Huésped eliminado correctamente.",
                icon: "success",
            });
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ??
                "No se pudo eliminar el huésped.";
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text: String(msg),
                icon: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleEditStart = (h: Huesped) => {
        setEditing({ ...h });
        setEditFile(null);
        setEditPreview(null);
    };
    const handleEditCancel = () => {
        setEditing(null);
        setEditFile(null);
        setEditPreview(null);
    };

    const handleEditSave = async () => {
        if (!editing) return;
        const errMsg = validateEditForm(editing);
        if (errMsg) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Dato inválido",
                text: errMsg,
                icon: "error",
            });
            return;
        }

        try {
            setSaving(true);
            const fd = new FormData();
            fd.append("nombre_completo", editing.nombre_completo.trim());
            fd.append("tipo_documento", editing.tipo_documento.trim());
            fd.append("numero_documento", editing.numero_documento.trim());
            if (editing.celular?.trim())
                fd.append("celular", editing.celular.trim());
            if (editing.email?.trim()) fd.append("email", editing.email.trim());
            if (editFile) fd.append("imagen_documento_huesped", editFile);

            await fetchAxiosToken({
                url: `${apiBase}/updateHuesped/${editing.id}`,
                method: "put",
                body: fd,
            });

            await getAll();
            setEditing(null);
            setEditFile(null);
            setEditPreview(null);

            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Actualizado",
                text: "Huésped actualizado correctamente.",
                icon: "success",
            });
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ??
                "No se pudo actualizar el huésped.";
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text: String(msg),
                icon: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const getImageUrl = (filename?: string) =>
        filename
            ? `${process.env.REACT_APP_PROXY_ARCHIVOS}/blu/v1${apiBase}/getImagen/${filename}`
            : "";

    return (
        <ResponsiveModalAbsolute
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}>
            <div className="max-w-3xl p-4 mx-auto">
                <h2 className="text-base font-medium text-center">
                    Huéspedes de la reserva
                </h2>
                <hr className="w-full mb-4 bg-gray-300" />

                {/* Botón para mostrar/ocultar formulario */}
                <div className="flex justify-center mb-3">
                    <button
                        onClick={() => setShowCreate((s) => !s)}
                        className="h-10 px-5 rounded-lg text-white bg-[#1C4CBB] hover:opacity-90">
                        {showCreate ? "Cancelar" : "Agregar nuevo huésped"}
                    </button>
                </div>

                {/* Formulario con animación y layout lineal */}
                <div
                    className={[
                        "overflow-hidden transition-all duration-300",
                        showCreate
                            ? "opacity-100 translate-y-0 max-h-[1200px]"
                            : "opacity-0 -translate-y-2 max-h-0 pointer-events-none",
                    ].join(" ")}>
                    <div className="p-4 mb-6 bg-[#FAFAFA] rounded-lg border border-[#E9E9E9]">
                        <h3 className="mb-3 font-medium">Nuevo huésped</h3>

                        {/* Fila 1: Nombre + Celular */}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="flex flex-col gap-1">
                                <label
                                    htmlFor="nombreCompleto"
                                    className="text-sm font-medium text-[#3C3C3C]">
                                    Nombre completo
                                </label>
                                <input
                                    id="nombreCompleto"
                                    type="text"
                                    placeholder="Ej: Sofía Sandoval"
                                    className={CONTROL}
                                    value={createForm.nombre_completo}
                                    onChange={(e) =>
                                        setCreateForm((p) => ({
                                            ...p,
                                            nombre_completo: e.target.value,
                                        }))
                                    }
                                    autoComplete="name"
                                    aria-required="true"
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label
                                    htmlFor="celular"
                                    className="text-sm font-medium text-[#3C3C3C]">
                                    Celular{" "}
                                    <span className="font-normal text-gray-400">
                                        (opcional)
                                    </span>
                                </label>
                                <input
                                    id="celular"
                                    type="tel"
                                    placeholder="Ej: 59161234567"
                                    className={CONTROL}
                                    value={createForm.celular}
                                    onChange={(e) =>
                                        setCreateForm((p) => ({
                                            ...p,
                                            celular: e.target.value,
                                        }))
                                    }
                                    inputMode="tel"
                                    autoComplete="tel"
                                />
                            </div>
                        </div>

                        {/* Fila 2: Tipo doc + Número doc */}
                        <div className="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-2">
                            <div className="flex flex-col gap-1">
                                <label
                                    htmlFor="tipoDocumento"
                                    className="text-sm font-medium text-[#3C3C3C]">
                                    Tipo de documento
                                </label>
                                <select
                                    id="tipoDocumento"
                                    className={CONTROL}
                                    value={createForm.tipo_documento}
                                    onChange={(e) =>
                                        setCreateForm((p) => ({
                                            ...p,
                                            tipo_documento: e.target.value,
                                        }))
                                    }
                                    aria-required="true">
                                    <option value="" disabled>
                                        Selecciona
                                    </option>
                                    {TIPO_DOC_OPCIONES.map((opt) => (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label
                                    htmlFor="numeroDocumento"
                                    className="text-sm font-medium text-[#3C3C3C]">
                                    Número de documento
                                </label>
                                <input
                                    id="numeroDocumento"
                                    type="text"
                                    placeholder="Ej: 12345678"
                                    className={CONTROL}
                                    value={createForm.numero_documento}
                                    onChange={(e) =>
                                        setCreateForm((p) => ({
                                            ...p,
                                            numero_documento: e.target.value,
                                        }))
                                    }
                                    inputMode="numeric"
                                    autoComplete="off"
                                    aria-required="true"
                                />
                            </div>
                        </div>

                        {/* Fila 3: Imagen del documento */}
                        <div className="mt-3">
                            <label
                                htmlFor="imgDoc"
                                className="text-sm font-medium text-[#3C3C3C]">
                                Imagen del documento{" "}
                                <span className="font-normal text-gray-400">
                                    (opcional)
                                </span>
                            </label>
                            <div className="mt-1">
                                <input
                                    id="imgDoc"
                                    type="file"
                                    accept="image/*"
                                    className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#1C4CBB] file:text-white file:cursor-pointer hover:file:opacity-90"
                                    onChange={(e) =>
                                        setCreateFile(
                                            e.target.files?.[0] ?? null
                                        )
                                    }
                                />
                            </div>
                            {createPreview && (
                                <img
                                    src={createPreview}
                                    alt="Preview documento"
                                    className="h-20 mt-2 rounded border border-[#E9E9E9] object-cover"
                                />
                            )}
                        </div>

                        {/* Fila 4: Email */}
                        <div className="mt-3">
                            <label
                                htmlFor="email"
                                className="text-sm font-medium text-[#3C3C3C]">
                                Email{" "}
                                <span className="font-normal text-gray-400">
                                    (opcional)
                                </span>
                            </label>
                            <input
                                id="email"
                                type="email"
                                placeholder="Ej: persona@correo.com"
                                className={CONTROL}
                                value={createForm.email}
                                onChange={(e) =>
                                    setCreateForm((p) => ({
                                        ...p,
                                        email: e.target.value,
                                    }))
                                }
                                autoComplete="email"
                            />
                        </div>

                        <div className="flex justify-center mt-4">
                            <button
                                disabled={saving}
                                onClick={handleCreate}
                                className="h-10 px-5 text-white rounded-lg bg-[#1C4CBB] hover:opacity-90 disabled:opacity-60">
                                {saving ? "Guardando..." : "Agregar huésped"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Listado */}
                <div className="w-full">
                    <h3 className="mb-2 font-medium">Listado</h3>
                    <div className="w-full overflow-x-auto rounded-lg border border-[#E9E9E9]">
                        {loading ? (
                            <div className="p-4">
                                <Spinner />
                            </div>
                        ) : list.length === 0 ? (
                            <div className="p-4 text-sm text-center text-gray-500">
                                No hay huéspedes registrados aún.
                            </div>
                        ) : (
                            <table className="w-full min-w-[980px] text-sm whitespace-nowrap">
                                <thead className="text-left bg-[#FAFAFA]">
                                    <tr>
                                        <th className="px-3 py-2">
                                            Nombre completo
                                        </th>
                                        <th className="px-3 py-2">Tipo doc.</th>
                                        <th className="px-3 py-2">Nro. doc.</th>
                                        <th className="px-3 py-2">Celular</th>
                                        <th className="px-3 py-2">Email</th>
                                        <th className="px-3 py-2">Documento</th>
                                        <th className="px-3 py-2 text-center">
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map((h) =>
                                        editing?.id === h.id ? (
                                            <tr
                                                key={h.id}
                                                className="align-middle border-top">
                                                <td className="px-3 py-2">
                                                    <input
                                                        className={CONTROL}
                                                        value={
                                                            editing.nombre_completo
                                                        }
                                                        onChange={(e) =>
                                                            setEditing((p) =>
                                                                p
                                                                    ? {
                                                                          ...p,
                                                                          nombre_completo:
                                                                              e
                                                                                  .target
                                                                                  .value,
                                                                      }
                                                                    : p
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <select
                                                        className={CONTROL}
                                                        value={
                                                            editing.tipo_documento
                                                        }
                                                        onChange={(e) =>
                                                            setEditing((p) =>
                                                                p
                                                                    ? {
                                                                          ...p,
                                                                          tipo_documento:
                                                                              e
                                                                                  .target
                                                                                  .value,
                                                                      }
                                                                    : p
                                                            )
                                                        }>
                                                        {TIPO_DOC_OPCIONES.map(
                                                            (opt) => (
                                                                <option
                                                                    key={opt}
                                                                    value={opt}>
                                                                    {opt}
                                                                </option>
                                                            )
                                                        )}
                                                    </select>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        className={CONTROL}
                                                        value={
                                                            editing.numero_documento
                                                        }
                                                        onChange={(e) =>
                                                            setEditing((p) =>
                                                                p
                                                                    ? {
                                                                          ...p,
                                                                          numero_documento:
                                                                              e
                                                                                  .target
                                                                                  .value,
                                                                      }
                                                                    : p
                                                            )
                                                        }
                                                        inputMode="numeric"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        className={CONTROL}
                                                        value={
                                                            editing.celular ??
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            setEditing((p) =>
                                                                p
                                                                    ? {
                                                                          ...p,
                                                                          celular:
                                                                              e
                                                                                  .target
                                                                                  .value,
                                                                      }
                                                                    : p
                                                            )
                                                        }
                                                        inputMode="tel"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        className={CONTROL}
                                                        value={
                                                            editing.email ?? ""
                                                        }
                                                        onChange={(e) =>
                                                            setEditing((p) =>
                                                                p
                                                                    ? {
                                                                          ...p,
                                                                          email: e
                                                                              .target
                                                                              .value,
                                                                      }
                                                                    : p
                                                            )
                                                        }
                                                        inputMode="email"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-3">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) =>
                                                                setEditFile(
                                                                    e.target
                                                                        .files?.[0] ??
                                                                        null
                                                                )
                                                            }
                                                            className="text-xs file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-[#1C4CBB] file:text-white file:cursor-pointer hover:file:opacity-90"
                                                        />
                                                        {(editPreview ||
                                                            (editing.imagen_documento &&
                                                                getImageUrl(
                                                                    editing.imagen_documento
                                                                ))) && (
                                                            <img
                                                                src={
                                                                    editPreview ||
                                                                    getImageUrl(
                                                                        editing.imagen_documento
                                                                    )
                                                                }
                                                                alt="Doc"
                                                                className="object-cover w-10 h-10 border rounded cursor-pointer"
                                                                onClick={() =>
                                                                    setViewImageUrl(
                                                                        editPreview ||
                                                                            getImageUrl(
                                                                                editing.imagen_documento
                                                                            )
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center justify-center gap-2 ml-8">
                                                        <button
                                                            onClick={
                                                                handleEditSave
                                                            }
                                                            disabled={saving}
                                                            className="h-10 px-4 text-white bg-[#1C4CBB] rounded-lg hover:opacity-90 disabled:opacity-60">
                                                            Guardar
                                                        </button>
                                                        <button
                                                            onClick={
                                                                handleEditCancel
                                                            }
                                                            className="h-10 px-4 text-[#1C4CBB] border border-[#1C4CBB] rounded-lg hover:opacity-90">
                                                            Cancelar
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr
                                                key={h.id}
                                                className="align-middle border-t">
                                                <td className="px-3 py-2">
                                                    {h.nombre_completo}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {h.tipo_documento}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {h.numero_documento}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {h.celular}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {h.email}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {h.imagen_documento ? (
                                                        <button
                                                            className="px-3 py-1 text-white rounded-lg bg-[#1C4CBB] hover:opacity-90"
                                                            onClick={() =>
                                                                setViewImageUrl(
                                                                    getImageUrl(
                                                                        h.imagen_documento
                                                                    )
                                                                )
                                                            }>
                                                            Ver
                                                        </button>
                                                    ) : (
                                                        <span className="text-gray-400">
                                                            —
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() =>
                                                                handleEditStart(
                                                                    h
                                                                )
                                                            }
                                                            className="h-10 px-4 text-white bg-blue-500 rounded-lg hover:opacity-90">
                                                            Editar
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleDelete(
                                                                    h.id
                                                                )
                                                            }
                                                            className="h-10 px-4 text-white bg-red-500 rounded-lg hover:opacity-90">
                                                            Eliminar
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Cerrar */}
                <div className="flex justify-center mt-5">
                    <button
                        onClick={() => setModalVisible(false)}
                        className="h-10 px-5 text-[#1C4CBB] border border-[#1C4CBB] rounded-lg hover:opacity-90">
                        Cerrar
                    </button>
                </div>
            </div>

            {/* Visor de imagen a pantalla completa (Portal al <body>) */}
            {viewImageUrl &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
                        onClick={() => setViewImageUrl(null)}>
                        <img
                            src={viewImageUrl}
                            alt="Documento"
                            className="max-h-[85vh] max-w-[90vw] rounded-lg shadow-2xl object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button
                            className="absolute px-3 py-1 text-sm text-black bg-white rounded-full shadow top-4 right-4"
                            onClick={() => setViewImageUrl(null)}>
                            Cerrar
                        </button>
                    </div>,
                    document.body
                )}
        </ResponsiveModalAbsolute>
    );
};
